import { useMemo } from 'react';
import { TFunction } from 'react-i18next';
import {
  emailRegex,
  externalIdRegex,
  facebookRegex,
  ibanRegex,
  idsRegex,
  instagramRegex,
  nameRegex,
  numbersOnlyRegex,
  passwordRegex,
  phoneRegex,
  skuRegex,
  slugRegex,
  taxCodeRegex,
  taxIpnRegex,
  tiktokRegex,
  twitterRegex,
  urlRegex,
  youtubeRegex,
  zipCodeRegex,
} from '@/constants/regex';
import { TFormValidatorRule } from '@/types';
import { FilterType } from '@/models/filters.model';
import { IProductFilter } from '@/models/products.model';

const getRequiredRule = (t: TFunction) => ({
  required: true,
  message: t('validation_message_required', { ns: 'validations' }),
});

const getTextLengthValidationRule = (t: TFunction, max: number, min?: number) => {
  const messageKey = min ? 'validation_text_message_length' : 'validation_message_max_length';

  return {
    min,
    max,
    message: t(messageKey, { ns: 'validations', min, max }),
  };
};

const getArrayLengthValidationRule = (t: TFunction, max: number, min?: number) => {
  const messageKey = min ? 'validation_message_array_length' : 'validation_message_array_max_length';

  return {
    min,
    max,
    type: 'array' as const,
    message: t(messageKey, { ns: 'validations', min, max }),
  };
};

const getSliderValueValidationRule = ({
  t,
  min,
  max,
  customValue,
}: {
  t: TFunction;
  customValue: string | string[];
  min?: string;
  max?: string;
}): {
  isValid: boolean;
  errorMessage?: string;
} => {
  const minNum = Number(min);
  const maxNum = Number(max);
  const customNum = Number(customValue);

  if (isNaN(minNum) || isNaN(maxNum) || isNaN(customNum)) {
    return {
      isValid: false,
      errorMessage: t('invalid_number_format'),
    };
  }

  if (customNum < minNum || customNum > maxNum) {
    return {
      isValid: false,
      errorMessage: t('value_out_of_range', { min: minNum, max: maxNum }),
    };
  }

  return { isValid: true };
};

export const useGetRequiredRule = ({ t }: { t: TFunction }) => useMemo(() => getRequiredRule(t), [t]);

export const useArrayLengthValidationRule = ({ t, min, max }: { t: TFunction; max: number; min?: number }) =>
  useMemo(() => getArrayLengthValidationRule(t, max, min), [t, max, min]);

export const useLengthValidationRule = ({ t, min, max }: { t: TFunction; max: number; min?: number }) =>
  useMemo(() => getTextLengthValidationRule(t, max, min), [t, max, min]);

export const useGetEmailRule = ({ t }: { t: TFunction }) =>
  useMemo(
    () => ({
      pattern: emailRegex,
      message: t('validation_message_email', { ns: 'validations' }),
    }),
    [t],
  );

export const useGetTaxCodeRule = ({ t }: { t: TFunction }) =>
  useMemo(
    () => ({
      pattern: taxCodeRegex,
      message: t('validation_invalid_format', { ns: 'validations' }),
    }),
    [t],
  );

export const useGetIpn = ({ t }: { t: TFunction }) =>
  useMemo(
    () => ({
      pattern: taxIpnRegex,
      message: t('validation_invalid_format', { ns: 'validations' }),
    }),
    [t],
  );

export const useGetPasswordRule = ({ t }: { t: TFunction }) =>
  useMemo(
    () => ({
      pattern: passwordRegex,
      message: t('validation_password_message', { ns: 'validations' }),
    }),
    [t],
  );

export const useGetIdsRule = ({ t }: { t: TFunction }) =>
  useMemo(
    () => ({
      pattern: idsRegex,
      message: t('validation_fop_ids_message', { ns: 'validations' }),
    }),
    [t],
  );

export const useGetFullNameRule = ({ t }: { t: TFunction }) =>
  useMemo(
    () => ({
      validator: (_: unknown, value: string) => {
        if (value == null || String(value).trim() === '') {
          return Promise.resolve();
        }

        if (nameRegex.test(String(value).trimEnd())) {
          return Promise.resolve();
        }

        return Promise.reject(new Error(t('validation_message_full_name', { ns: 'validations' })));
      },
    }),
    [t],
  );

export const useGetZipCodeRule = ({ t }: { t: TFunction }) =>
  useMemo(
    () => ({
      pattern: zipCodeRegex,
      message: t('validation_message_zip_code', { ns: 'validations' }),
    }),
    [t],
  );

export const useGetPhoneRule = ({ t }: { t: TFunction }) =>
  useMemo(
    () => ({
      pattern: phoneRegex,
      message: t('validation_message_phone', { ns: 'validations' }),
    }),
    [t],
  );

export const useGetIbanRule = ({ t }: { t: TFunction }) =>
  useMemo(
    () => ({
      pattern: ibanRegex,
      message: t('validation_invalid_format', { ns: 'validations' }),
    }),
    [t],
  );

export const useGetUrlRule = ({ t }: { t: TFunction }) =>
  useMemo(
    () => ({
      pattern: urlRegex,
      message: t('validation_message_url', { ns: 'validations' }),
    }),
    [t],
  );

export const useGetFacebookRule = ({ t }: { t: TFunction }) =>
  useMemo(
    () => ({
      pattern: facebookRegex,
      message: t('validation_message_url_facebook', { ns: 'validations' }),
    }),
    [t],
  );

export const useGetInstagramRule = ({ t }: { t: TFunction }) =>
  useMemo(
    () => ({
      pattern: instagramRegex,
      message: t('validation_message_url_instagram', { ns: 'validations' }),
    }),
    [t],
  );

export const useGetTwitterRule = ({ t }: { t: TFunction }) =>
  useMemo(
    () => ({
      pattern: twitterRegex,
      message: t('validation_message_url_twitter', { ns: 'validations' }),
    }),
    [t],
  );

export const useGetTiktokRule = ({ t }: { t: TFunction }) =>
  useMemo(
    () => ({
      pattern: tiktokRegex,
      message: t('validation_message_url_tiktok', { ns: 'validations' }),
    }),
    [t],
  );

export const useGetYoutubeRule = ({ t }: { t: TFunction }) =>
  useMemo(
    () => ({
      pattern: youtubeRegex,
      message: t('validation_message_url_youtube', { ns: 'validations' }),
    }),
    [t],
  );

export const useGetNumbersOnlyRule = ({ t }: { t: TFunction }) =>
  useMemo(
    () => ({
      pattern: numbersOnlyRegex,
      message: t('validation_numbers_only', { ns: 'validations' }),
    }),
    [t],
  );

export const useDependentRequiredFieldsValidator =
  <TFormFields extends string, TFormValues extends Partial<Record<TFormFields, unknown>>>(
    t: TFunction,
    fieldName: TFormFields,
    dependencyFieldName: TFormFields,
  ): TFormValidatorRule =>
  ({ getFieldsValue }) => ({
    validator() {
      const values: TFormValues = getFieldsValue();
      const { [fieldName]: fieldNameValue, [dependencyFieldName]: dependencyFieldNameValue } = values;

      if (!fieldNameValue && !dependencyFieldNameValue) {
        return Promise.resolve();
      }

      if (!fieldNameValue) {
        return Promise.reject(t('validation_message_required', { ns: 'validations' }));
      }

      return Promise.resolve();
    },
  });

export const useGetSlugRule = ({ t }: { t: TFunction }) =>
  useMemo(
    () => ({
      pattern: slugRegex,
      message: t('validation_slug', { ns: 'validations' }),
    }),
    [t],
  );

export const useGetSkuRule = ({ t }: { t: TFunction }) =>
  useMemo(
    () => ({
      pattern: skuRegex,
      message: t('validation_sku', { ns: 'validations' }),
    }),
    [t],
  );

export const useGetExternalIdRule = ({ t }: { t: TFunction }) =>
  useMemo(
    () => ({
      pattern: externalIdRegex,
      message: t('validation_external_id', { ns: 'validations' }),
    }),
    [t],
  );

export const useGetDiscountPriceRule =
  <TFormFields extends string, TFormValues extends Partial<Record<TFormFields, unknown>>>(
    t: TFunction,
    fieldName: TFormFields,
    dependencyFieldName: TFormFields,
  ): TFormValidatorRule =>
  ({ getFieldsValue }) => ({
    validator() {
      const values: TFormValues = getFieldsValue();
      const { [fieldName]: fieldNameValue, [dependencyFieldName]: dependencyFieldNameValue } = values;

      if (Number(fieldNameValue) >= Number(dependencyFieldNameValue)) {
        return Promise.reject(t('discount_price_validation_message', { ns: 'products' }));
      }

      return Promise.resolve();
    },
  });

export const useGetMinMaxRule =
  <TFormFields extends string, TFormValues extends Partial<Record<TFormFields, unknown>>>(
    t: TFunction,
    minFieldName: TFormFields,
    maxFieldName: TFormFields,
    typeFieldName: TFormFields,
    isEdit: boolean,
    initialMinValue?: number,
    initialMaxValue?: number,
  ): TFormValidatorRule =>
  ({ getFieldsValue, getFieldError, setFields }) => ({
    validator(_, value) {
      const values: TFormValues = getFieldsValue();
      const { [minFieldName]: minValue, [maxFieldName]: maxValue, [typeFieldName]: typeValue } = values;

      if (typeValue !== FilterType.SLIDER) {
        return Promise.resolve();
      }

      if (!minValue || !maxValue) {
        if (!value) {
          return Promise.reject(t('validation_message_required', { ns: 'validations' }));
        }
        return Promise.resolve();
      }

      if (isEdit) {
        if (initialMinValue !== undefined && Number(minValue) > initialMinValue) {
          return Promise.reject(
            t('validation_min_greater_than_initial', { ns: 'validations', initialMin: initialMinValue }),
          );
        }
        if (initialMaxValue !== undefined && Number(maxValue) < initialMaxValue) {
          return Promise.reject(
            t('validation_max_less_than_initial', { ns: 'validations', initialMax: initialMaxValue }),
          );
        }
      } else {
        if (Number(minValue) >= Number(maxValue)) {
          return Promise.reject(t('validation_min_must_be_less_than_max', { ns: 'validations' }));
        }
      }

      const minFieldError = getFieldError(minFieldName);
      const maxFieldError = getFieldError(maxFieldName);

      if (minFieldError.length > 0) {
        setFields([{ name: minFieldName, errors: [] }]);
      }
      if (maxFieldError.length > 0) {
        setFields([{ name: maxFieldName, errors: [] }]);
      }

      return Promise.resolve();
    },
    validateTrigger: ['onChange', 'onSubmit'],
  });

export const useSliderFilterValidation = ({ t }: { t: TFunction }) => {
  const getSliderValidationRule = (filter: IProductFilter) => {
    return {
      validator: (_: any, value: string[]) => {
        if (!value || filter.values.find((filterValue) => value.includes(filterValue.id))) return Promise.resolve();

        const validation = getSliderValueValidationRule({ t, customValue: value, min: filter?.min, max: filter?.max });

        if (!validation.isValid) {
          return Promise.reject(new Error(validation.errorMessage));
        }

        return Promise.resolve();
      },
      validateTrigger: ['onChange'],
    };
  };

  return getSliderValidationRule;
};
